import { AxiosInstance } from 'axios';

/**
 * The live state of the queues, as the hub broadcasts it and the REST endpoint returns it.
 * These types are kept apart from api.ts because that file is generated by nswag and gets
 * overwritten on the next generation.
 */

/** Which of the two pipelines a queue belongs to. */
export type QueuePipeline = 'ChargebeeToExact' | 'ExactToChargebee' | 'Unknown';

/** Which step within that pipeline. */
export type QueueStage = 'Webhook' | 'Ready' | 'Retry' | 'Poison' | 'Unknown';

export interface QueueMessagePreview {
    messageId: string;
    body: string;
    objectType?: string;
    eventType?: string;
    relationId?: string;
    insertedOn?: string;
    dequeueCount: number;
}

export interface QueueState {
    name: string;
    pipeline: QueuePipeline;
    stage: QueueStage;
    /** For a poison queue: the queue whose failures end up here. */
    poisonOf?: string | null;
    /** Whether requeueing is allowed to write to this queue. */
    canRequeueTo: boolean;
    /** Number of messages, or -1 when the queue could not be read. */
    depth: number;
    /** What is ready to be picked up; null when that cannot be determined exactly. */
    waiting?: number | null;
    /**
     * What nobody can see: either a function is holding it, or it failed and is waiting out
     * its turn before the next attempt.
     *
     * Azure draws no distinction between those two and offers no way to inspect an
     * invisible message, so this number on its own is not progress. `batchSize` is what
     * makes it readable: one instance holds at most that many, so everything above it is
     * waiting rather than working. This field used to be called `processing` and was
     * rendered as "picked up", which is how a queue that had been failing since the morning
     * came to be captioned "normal as long as it does not take long".
     */
    hidden?: number | null;
    /** Messages per minute; positive means draining, negative means filling up. */
    perMinute?: number | null;
    etaSeconds?: number | null;
    oldestInsertedOn?: string | null;
    /**
     * The highest number of attempts among the messages that could be seen. Measured,
     * unlike `maxAttempts`, which is the configured ceiling. Zero means none of them has
     * ever been handed out.
     */
    attemptsSeen: number;
    /** The app that should be reading this queue; null when nothing in the code does. */
    consumerApp?: string | null;
    /** The function within that app that reads it. */
    consumerFunction?: string | null;
    /** How many messages one instance of that function holds at a time. */
    batchSize?: number | null;
    /** How long a failed message stays invisible before its next attempt. */
    visibilityTimeoutSeconds?: number | null;
    /** After how many attempts the runtime moves a message to the poison queue. */
    maxAttempts?: number | null;
    /** How long a message that fails every time occupies this queue before poison. */
    worstCaseLifetimeSeconds?: number | null;
    /** Whether these settings are the runtime defaults because host.json says nothing. */
    settingsAreDefaults?: boolean;
    head: QueueMessagePreview[];
    error?: string | null;
}

export interface QueueSnapshot {
    customer: string;
    environment: string;
    sampledAt: string;
    queues: QueueState[];
    error?: string | null;
}

/** The state of the connection to the hub. */
export type QueueConnectionState =
    | 'idle'
    | 'connecting'
    | 'live'
    | 'reconnecting'
    | 'polling'
    | 'offline';

/**
 * The pipeline in plain language.
 *
 * A queue is named after where its messages came from; the question on this screen is
 * always where they still have to go. So queue "chargebee" does not hold what is going to
 * Chargebee but what came out of Chargebee and still has to reach Exact. That is exactly
 * the mix-up that makes an invoice vanish without a trace, which is why the destination is
 * what ends up in the label here.
 */
export const stageLabel = (queue: QueueState): string => {
    if (queue.stage === 'Poison') {
        return 'Mislukt';
    }

    if (queue.stage === 'Retry') {
        return 'Nieuwe poging';
    }

    if (queue.pipeline === 'ChargebeeToExact') {
        return queue.stage === 'Webhook'
            ? 'Binnengekomen uit Chargebee'
            : 'Klaar voor Exact';
    }

    if (queue.pipeline === 'ExactToChargebee') {
        return queue.stage === 'Webhook'
            ? 'Binnengekomen uit Exact'
            : 'Klaar voor Chargebee';
    }

    return queue.name;
};

/** Where the messages of this pipeline eventually end up. */
export const pipelineTitle = (pipeline: QueuePipeline): string => {
    switch (pipeline) {
        case 'ChargebeeToExact':
            return 'Chargebee naar Exact';
        case 'ExactToChargebee':
            return 'Exact naar Chargebee';
        default:
            return 'Overige queues';
    }
};

/** The system at the end of the pipeline. */
export const pipelineTarget = (pipeline: QueuePipeline): string | null => {
    switch (pipeline) {
        case 'ChargebeeToExact':
            return 'Exact';
        case 'ExactToChargebee':
            return 'Chargebee';
        default:
            return null;
    }
};

/**
 * The system the messages of this pipeline came out of.
 *
 * Drawn as the first node so the row reads as the chain it is, from one system to the other.
 * Without it the pipeline started at a queue named after the system it came from, which is
 * precisely the naming that makes people read `chargebee` as "going to Chargebee".
 */
export const pipelineSource = (pipeline: QueuePipeline): string | null => {
    switch (pipeline) {
        case 'ChargebeeToExact':
            return 'Chargebee';
        case 'ExactToChargebee':
            return 'Exact';
        default:
            return null;
    }
};

/**
 * Fetch the state in a single call. The hub is the normal route; this is the first render
 * and the fallback for networks the websocket cannot get through.
 */
export const getQueueSnapshot = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<QueueSnapshot> => {
    const response = await axios.get(
        `/QueueLog/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/queues/live`
    );

    // The axios instance was created with transformResponse: [], so response.data is a
    // string and has to be parsed here.
    return typeof response.data === 'string'
        ? (JSON.parse(response.data) as QueueSnapshot)
        : (response.data as QueueSnapshot);
};

/** A duration in seconds rendered as the single largest unit, number and unit together: "45s", "3m", "2u", "3d". */
export const formatDuration = (seconds?: number | null): string | null => {
    if (seconds === null || seconds === undefined || seconds < 0) {
        return null;
    }

    if (seconds < 60) {
        return `${Math.round(seconds)}s`;
    }

    const minutes = Math.round(seconds / 60);

    if (minutes < 60) {
        return `${minutes}m`;
    }

    const hours = Math.round(minutes / 60);

    if (hours < 24) {
        return `${hours}u`;
    }

    return `${Math.round(hours / 24)}d`;
};

/** How long ago a moment was, written out briefly. */
export const formatAge = (
    iso?: string | null,
    now: number = Date.now()
): string | null => {
    if (!iso) {
        return null;
    }

    const then = Date.parse(iso);

    if (Number.isNaN(then)) {
        return null;
    }

    return formatDuration(Math.max(0, (now - then) / 1000));
};

/** Numbers with a dot as the thousands separator. */
export const formatCount = (value: number) => value.toLocaleString('nl-NL');

/**
 * From when standstill in a queue is more than a snapshot. Below this, a queue that just
 * received something is indistinguishable from a queue nobody reads, and the diagnosis
 * would raise a false alarm on every busy minute.
 */
const STUCK_AFTER_SECONDS = 15 * 60;

/** What is going on with a queue, in a single word. */
export type QueueVerdict =
    | 'unreadable'
    | 'parked'
    | 'draining'
    | 'failing'
    | 'backoff'
    | 'orphaned'
    | 'stuck'
    | 'waiting';

export interface QueueDiagnosis {
    verdict: QueueVerdict;
    /** The line on the card. Short enough to fit next to the counts. */
    label: string;
    /** What is wrong and what to do about it; the tooltip and the line in the detail. */
    hint: string;
    tone: 'neutral' | 'good' | 'bad';
}

/**
 * Where a queue stands, derived from the state the server sends along anyway.
 *
 * Azure cannot tell you whether a consumer is attached to a queue, nor let you look inside
 * an invisible message. What it does tell you is how often the messages at the front have
 * been handed out, how long the oldest one has been sitting there, and how many of them
 * nobody can see. Read against the batch size of the function that should be reading, those
 * four separate the cases that matter: messages being picked up and failing, messages
 * waiting out a failed attempt, and messages nobody is picking up at all. On a tile with
 * only a count those look identical, while the first is a data problem, the second is a
 * matter of patience or a nudge, and the third is a function that is not running.
 */
export const diagnoseQueue = (
    queue: QueueState,
    now: number = Date.now()
): QueueDiagnosis | null => {
    if (queue.depth < 0 || queue.error) {
        return {
            verdict: 'unreadable',
            tone: 'bad',
            label: 'kon niet gelezen worden',
            hint:
                queue.error ??
                'Azure Storage gaf geen antwoord voor deze queue, dus de stand is onbekend.',
        };
    }

    if (queue.depth === 0) {
        return null;
    }

    if (queue.stage === 'Poison') {
        return {
            verdict: 'parked',
            tone: 'bad',
            label: 'wacht op terugzetten',
            hint: 'Een mislukt-queue wordt door niets gelezen: deze berichten blijven hier staan tot iemand ze terugzet. Zoek eerst in het foutenlogboek waarom ze zijn misgelopen - is die oorzaak er nog, dan lopen ze na terugzetten meteen opnieuw stuk.',
        };
    }

    // Nothing in the code binds to this queue, so no amount of waiting or retrying moves
    // it. Worth settling first, because every verdict below reasons about a consumer.
    if (!queue.consumerApp) {
        return {
            verdict: 'orphaned',
            tone: 'bad',
            label: 'niemand leest deze queue',
            hint: `Er is geen queue-trigger die aan "${queue.name}" hangt. Meestal wijst een app-setting dan naar een andere queuenaam dan waar de berichten naartoe worden geschreven.`,
        };
    }

    const ageSeconds = queue.oldestInsertedOn
        ? Math.max(0, (now - Date.parse(queue.oldestInsertedOn)) / 1000)
        : null;

    if ((queue.perMinute ?? 0) > 0) {
        const eta = formatDuration(queue.etaSeconds);

        return {
            verdict: 'draining',
            tone: 'good',
            label: eta ? `leeg over ${eta}` : 'loopt leeg',
            hint: `Deze queue loopt leeg met ongeveer ${Math.round(
                queue.perMinute as number
            )} bericht(en) per minuut. Er is niets aan de hand; het duurt alleen even.`,
        };
    }

    // Handed out and still here: the consumer is running, but the processing fails.
    if (queue.attemptsSeen > 0) {
        const cap = queue.maxAttempts ?? 5;

        return {
            verdict: 'failing',
            tone: 'bad',
            label: `loopt stuk (poging ${queue.attemptsSeen} van ${cap})`,
            hint: `De berichten worden opgepakt, maar de verwerking mislukt en ze komen terug op de queue. Na ${cap} pogingen verhuizen ze naar ${queue.name}-poison. Waarom ze stuklopen staat in het foutenlogboek onder deze keten.`,
        };
    }

    /*
        Invisible messages beyond what one instance can hold.

        This is the case the previous version got wrong, and it got it wrong twice over. An
        invisible message yields no peek, so there was no attempt counter and no age to go
        on, and the verdict fell through to "waiting, which is normal" - on a queue where
        every message had already failed at least once. The batch size settles it: the
        consumer holds at most that many at a time, so the rest are serving out the wait
        before their next attempt.

        The honest caveat is scaling. Several instances can each hold a batch, so this
        ceiling is per instance; the hint says as much rather than pretending otherwise.
    */
    const hidden = queue.hidden ?? 0;
    const ceiling = queue.batchSize ?? 0;

    if (hidden > 0 && ceiling > 0 && hidden > ceiling) {
        const waitingForRetry = hidden - ceiling;

        const wait =
            (queue.visibilityTimeoutSeconds ?? 0) > 0
                ? ` Na een mislukte poging blijft een bericht ${formatDuration(
                      queue.visibilityTimeoutSeconds
                  )} onzichtbaar.`
                : '';

        const worst =
            (queue.worstCaseLifetimeSeconds ?? 0) > 0
                ? ` Blijft het misgaan, dan duurt het ${formatDuration(
                      queue.worstCaseLifetimeSeconds
                  )} voordat een bericht in ${queue.name}-poison belandt.`
                : '';

        return {
            verdict: 'backoff',
            tone: 'bad',
            label: `${formatCount(waitingForRetry)} wachten op nieuwe poging`,
            hint: `Deze berichten zijn onzichtbaar, en dat betekent hier niet dat er aan gewerkt wordt: "${queue.consumerFunction}" pakt er ${ceiling} per keer op, dus de rest heeft een mislukte poging achter zich.${wait}${worst} Bovenaan staat wat je hieraan kunt doen.`,
        };
    }

    // Never handed out and already old: nothing is attached to this queue.
    if (ageSeconds !== null && ageSeconds >= STUCK_AFTER_SECONDS) {
        return {
            verdict: 'stuck',
            tone: 'bad',
            label: 'niets pakt dit op',
            hint: `Geen van de berichten vooraan is ooit uitgedeeld en het oudste staat er al ${formatDuration(
                ageSeconds
            )}. Dat betekent vrijwel altijd dat "${
                queue.consumerFunction
            }" niet loopt: de function app staat stil, de functie is uitgezet, of de app-setting van die app wijst naar een andere queue. Terugzetten of opnieuw aanbieden helpt hier niets - er is niemand die het oppakt. Kijk bovenaan bij de function apps.`,
        };
    }

    const age = formatAge(queue.oldestInsertedOn, now);

    // What is left really is an ordinary wait: visible messages, none of them tried yet,
    // and not old enough to mean anything. Everything that used to land here by accident
    // now has a verdict of its own above.
    return {
        verdict: 'waiting',
        tone: 'neutral',
        label: age ? `oudste: ${age}` : 'klaar om opgepakt te worden',
        hint: `Deze berichten staan klaar en zijn nog niet uitgedeeld aan "${queue.consumerFunction}". Dat is normaal zolang het niet lang duurt.`,
    };
};
